import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import { LayoutDashboard, Users, Plane, Ship, Tag, CalendarClock, LogOut, Menu, X, ExternalLink, Wallet } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/admin/AuthContext";
import { inputClass, labelClass } from "@/pages/EnquiryPage";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "admin-nav-dashboard" },
  { to: "/admin/leads", label: "Leads", icon: Users, testid: "admin-nav-leads" },
  { to: "/admin/leads?tag=buyer", label: "Buyers", icon: Tag, testid: "admin-nav-buyers" },
  { to: "/admin/leads?tag=seller", label: "Sellers", icon: Tag, testid: "admin-nav-sellers" },
  { to: "/admin/listings", label: "Listings", icon: Plane, testid: "admin-nav-listings" },
  { to: "/admin/empty-legs", label: "Empty legs", icon: CalendarClock, testid: "admin-nav-empty-legs" },
  { to: "/admin/members", label: "Members", icon: Ship, testid: "admin-nav-members" },
  { to: "/admin/jet-accounts", label: "Jet Accounts", icon: Wallet, testid: "admin-nav-jet-accounts" },
];

export const AdminLayout = () => {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const links = (
    <nav className="flex flex-col gap-1">
      {NAV.map((n) => (
        <NavLink key={n.to} to={n.to} end={n.end} onClick={() => setOpen(false)} data-testid={n.testid}
                 className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm transition-colors ${isActive && !n.to.includes("?") ? "bg-gold/10 text-gold" : "text-white/60 hover:text-white hover:bg-white/5"}`}>
          <n.icon size={15} /> {n.label}
        </NavLink>
      ))}
    </nav>
  );

  return (
    <div className="min-h-screen bg-obsidian text-white flex" data-testid="admin-layout">
      <aside className="hidden lg:flex w-60 shrink-0 flex-col border-r border-white/10 bg-surface p-5 sticky top-0 h-screen">
        <Link to="/" className="font-serif text-xl mb-1">Aero Vista <span className="text-gold">Club</span></Link>
        <p className="text-[10px] uppercase tracking-[0.25em] text-white/40 mb-8">CRM</p>
        {links}
        <div className="mt-auto pt-6 border-t border-white/10 text-xs text-white/50">
          <p className="truncate">{user?.email}</p>
          <div className="flex gap-4 mt-3">
            <a href="/" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:text-white" data-testid="admin-view-site-link">Site <ExternalLink size={11} /></a>
            <button onClick={async () => { await logout(); navigate("/admin/login"); }} className="inline-flex items-center gap-1 hover:text-white" data-testid="admin-logout-button"><LogOut size={11} /> Log out</button>
          </div>
        </div>
      </aside>
      <div className="flex-1 min-w-0">
        <header className="lg:hidden flex items-center justify-between px-5 h-14 border-b border-white/10 bg-surface sticky top-0 z-30">
          <Link to="/admin" className="font-serif text-lg">AVC <span className="text-gold">CRM</span></Link>
          <button onClick={() => setOpen(!open)} aria-label="Menu" data-testid="admin-mobile-menu-toggle">{open ? <X size={20} /> : <Menu size={20} />}</button>
        </header>
        {open && <div className="lg:hidden border-b border-white/10 bg-surface p-4">{links}<button onClick={async () => { await logout(); navigate("/admin/login"); }} className="mt-4 text-xs text-white/50">Log out</button></div>}
        <main className="p-5 lg:p-10 max-w-7xl"><Outlet /></main>
      </div>
    </div>
  );
};

export default function AdminLogin() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (user) navigate("/admin", { replace: true }); }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await login(form.email, form.password); navigate("/admin", { replace: true }); }
    catch (err) { toast.error(err.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-obsidian flex items-center justify-center p-6" data-testid="admin-login-page">
      <form onSubmit={submit} className="w-full max-w-sm bg-surface border border-white/10 rounded-sm p-8">
        <p className="font-serif text-2xl text-white">Aero Vista <span className="text-gold">Club</span></p>
        <p className="text-[10px] uppercase tracking-[0.25em] text-white/40 mt-1 mb-8">Team login</p>
        <div className="space-y-4">
          <div><label className={labelClass}>Email</label><input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputClass} data-testid="admin-login-email-input" /></div>
          <div><label className={labelClass}>Password</label><input type="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={inputClass} data-testid="admin-login-password-input" /></div>
        </div>
        <button type="submit" disabled={busy} data-testid="admin-login-submit-button" className="mt-8 w-full bg-gold text-black text-xs uppercase tracking-[0.2em] py-3.5 rounded-sm hover:bg-gold-light disabled:opacity-50 transition-colors">{busy ? "Signing in..." : "Sign in"}</button>
        <Link to="/" className="block mt-6 text-center text-xs text-white/40 hover:text-white" data-testid="admin-login-back-link">Back to site</Link>
      </form>
    </div>
  );
}
